import { useEffect, useState, useCallback } from 'react';
import { CreatedUnavailableDateResponse, UnavailableDate } from '../../types/unavailable-dates';
import { unavailableEndpoints } from '../../services/api/endpoints/unavailable-dates.endpoint';
import dayjs from 'dayjs';

export const useUnavailableDates = (
  orgId: string,
  resourceId: string,
  startDate: string,
  endDate: string
) => {
  const [unavailableDates, setUnavailableDates] = useState<CreatedUnavailableDateResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnavailableDates = useCallback(async () => {
    if (!orgId || !resourceId || !startDate || !endDate) return;

    setIsLoading(true);
    setError(null);

    try {
      const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
      const data = await unavailableEndpoints.getUnavailableDates(
        orgId,
        resourceId,
        formattedStartDate,
        formattedEndDate
      );
      setUnavailableDates(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch unavailable dates');
    } finally {
      setIsLoading(false);
    }
  }, [orgId, resourceId, startDate, endDate]);

  useEffect(() => {
    fetchUnavailableDates();
  }, [fetchUnavailableDates]);

  const createUnavailableDate = useCallback(
    async (payload: UnavailableDate): Promise<boolean> => {
      try {
        const newDate = await unavailableEndpoints.createUnavailableDates(orgId, payload);
        setUnavailableDates(prev => [...prev, newDate]);
        return true;
      } catch (err: any) {
        setError(err?.message || 'Failed to create unavailable date');
        return false;
      }
    },
    [orgId]
  );

  const deleteUnavailableDate = useCallback(
    async (bookedDate: string): Promise<boolean> => {
      try {
        const success = await unavailableEndpoints.deleteUnavailableDate(
          orgId,
          resourceId,
          bookedDate
        );
        if (success) {
          setUnavailableDates(prev => prev.filter(date => date.bookedDate !== bookedDate));
          return true;
        }
        return false;
      } catch (err: any) {
        setError(err?.message || 'Failed to delete unavailable date');
        return false;
      }
    },
    [orgId, resourceId]
  );

  return {
    unavailableDates,
    isLoading,
    error,
    refetch: fetchUnavailableDates,
    createUnavailableDate,
    deleteUnavailableDate,
  };
};
